import { AssetUrls } from 'config/assets';
import { EmailNotificationField } from 'types/interfaces/message';

export type NotificationRowKey = keyof typeof AssetUrls.emailPreviews;

export interface NotificationRow {
  key: NotificationRowKey;
  label: string;
  sublabel?: string;
  /** The preference this toggle writes. */
  field: EmailNotificationField;
  /** Subject line of the example email in the preview. */
  subject: string;
  /** One sentence on what turning it off costs; shown under the preview. */
  offNote: string;
  /** Present on the three response-critical toggles: turning them OFF asks first. */
  warning?: { what: string; consequence: string };
  /** Shown only to users enrolled in the partner program. */
  partnersOnly?: boolean;
}

export interface NotificationGroup {
  label: string;
  rows: NotificationRow[];
}

/**
 * The 13 email toggles, in 4 groups. Labels, subjects and off-notes are the
 * ticket's final copy (Asana 1219194741720364); the subjects are the ones the
 * backend actually sends.
 */
export const NOTIFICATION_GROUPS: NotificationGroup[] = [
  {
    label: 'Messages',
    rows: [
      {
        key: 'msg',
        label: 'Direct messages from traders',
        field: 'emailOnNewMessage',
        subject: 'You have a new message from OtherTrader on TradeMagic',
        offNote: "Turn this off and you'll only see new messages when you open TradeMagic.",
        warning: {
          what: 'direct message emails',
          consequence:
            "If a trading partner messages you and you don't reply in time, it can count against you if a case is opened.",
        },
      },
      {
        key: 'case',
        label: 'Replies to my support cases',
        field: 'emailOnCaseReply',
        subject: 'You have a new message about your case on TradeMagic',
        offNote: "Turn this off and you won't be emailed when our team replies about a case.",
        warning: {
          what: 'support case emails',
          consequence:
            "Our team may need a reply from you to resolve a case. Missing it can delay the case or affect how it's resolved.",
        },
      },
    ],
  },
  {
    label: 'Trades',
    rows: [
      {
        key: 'trade',
        label: 'Someone starts a trade and sends me cards',
        field: 'emailOnNewTrade',
        subject: 'You have new cards coming from OtherTrader on TradeMagic',
        offNote:
          "Turn this off and you won't be emailed when a trader starts sending you cards, so you could miss incoming mail.",
      },
      {
        key: 'added',
        label: "Cards are added to a trade I'm receiving",
        field: 'emailOnCardsAdded',
        subject: 'OtherTrader added cards to Trade #10482 on TradeMagic',
        offNote: "Turn this off and you won't be told when a sender adds more cards to a trade.",
      },
      {
        key: 'shipped',
        label: "Cards I'm expecting have shipped",
        field: 'emailOnShipped',
        subject: 'Your package from OtherTrader has shipped',
        offNote: "Turn this off and you won't be told when incoming cards are in the mail.",
      },
      {
        key: 'funding',
        label: 'A pending trade opens or is cancelled',
        sublabel: 'When a trade that depends on another trade can go ahead',
        field: 'emailOnTradeFunding',
        subject: 'Your trade is now open on TradeMagic',
        offNote: "Turn this off and you won't be told when a waiting trade becomes ready to ship.",
      },
      {
        key: 'system',
        label: 'Reminders to ship or complete a trade',
        field: 'emailOnTradeReminders',
        subject: 'Reminder: Ship Your Cards',
        offNote: "Turn this off and you won't get reminders about trades waiting on you.",
        warning: {
          what: 'trade reminder emails',
          consequence:
            'These reminders are how we flag trades that are waiting on you. Missing them can count against you if a case is opened.',
        },
      },
    ],
  },
  {
    label: 'Trade offers',
    rows: [
      {
        key: 'offer-received',
        label: 'I receive a trade offer',
        field: 'emailOnOfferReceived',
        subject: 'New trade offer from OtherTrader',
        offNote: 'Turn this off and offers can expire before you see them.',
      },
      {
        key: 'offer-result',
        label: 'My offer is accepted or declined',
        field: 'emailOnOfferResult',
        subject: 'OtherTrader accepted your trade offer',
        offNote: "Turn this off and you won't know when an offer is ready to ship.",
      },
      {
        key: 'offer-expired',
        label: "My offer expires or can't go through",
        field: 'emailOnOfferExpired',
        subject: 'Your trade offer with OtherTrader expired',
        offNote: "Turn this off and you won't be told when held points come back.",
      },
    ],
  },
  {
    label: 'From TradeMagic',
    rows: [
      {
        key: 'setup',
        label: 'Setup tips',
        sublabel: 'Build your want list, verify your email and phone, your first trade',
        field: 'emailOnSetupTips',
        subject: 'Step 1: Build Your Want List',
        offNote: "Turn this off and you'll stop getting tips for getting started.",
      },
      {
        key: 'partner',
        label: 'Partner program updates',
        sublabel: 'Referral conversions and payouts',
        field: 'emailOnPartnerUpdates',
        subject: 'Someone you referred just joined Elite!',
        offNote: "Turn this off and you won't be emailed about referral earnings or payouts.",
        partnersOnly: true,
      },
      {
        key: 'features',
        label: 'New features and blog posts',
        sublabel: 'About one email a month',
        field: 'emailOnNewFeatures',
        subject: 'New on TradeMagic',
        offNote: "Turn this off and you'll miss announcements about what's new.",
      },
    ],
  },
];
